import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { encodeQuizState, decodeQuizState, encodeArray, decodeArray } from '../../utils/secureParams';
import { auth } from '../../firebase';
import UnansweredWarningModal from './UnansweredWarningModal';
import { getQuizBySlug, createQuizAttempt, updateQuiz, increment } from '../../services/firestoreService';
import { calculateMultipleChoiceScore } from '../MakeQuiz/MakeQuiz';
import { SafeMathText } from '../MakeQuiz/MathRenderer';
import { useNotifications } from '../../contexts/NotificationContext';
import {
  Clock, ChevronLeft, ChevronRight, CheckCircle,
  AlertCircle, Flag, X, Menu, Lightbulb, ChevronDown, RefreshCw
} from 'lucide-react';
import badgeAwardIcon from '../../Assets/icons/badge-award-medal-svgrepo-com.svg';
import failAlertIcon from '../../Assets/icons/fail-error-alert-notification-warning-svgrepo-com.svg';

const TakeQuiz = () => {
  const { quizSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { notify } = useNotifications();

  // Core State
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0); // Track elapsed time for untimed quizzes
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNavigator, setShowNavigator] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [hintExpanded, setHintExpanded] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [attemptId, setAttemptId] = useState(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showUnansweredWarning, setShowUnansweredWarning] = useState(false);
  const [showSkipWarning, setShowSkipWarning] = useState(false);
  const [isNavigatingToReview, setIsNavigatingToReview] = useState(false);

  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const storageKey = `quiz_state_${quizSlug}_${auth.currentUser?.uid}`;
  const quizActiveRef = useRef(true);
  const submissionStartedRef = useRef(false);

  // Prevent navigation away with warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (quizActiveRef.current) {
        e.preventDefault();
        e.returnValue = 'Warning: Leaving now will end your quiz session and submit your current progress. You may receive a score of 0 if scored.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Load quiz data
  useEffect(() => {
    const loadQuiz = async () => {
      try {
        // Skip security check if submission has already started
        if (submissionStartedRef.current) {
          return;
        }

        // Security Check: Verify session token or saved state exists
        const sessionToken = location.state?.sessionToken;
        const storedToken = sessionStorage.getItem(`quiz_session_${quizSlug}`);
        const savedState = localStorage.getItem(storageKey);

        // Allow access if:
        // 1. Valid session token from navigation state
        // 2. Valid session token in sessionStorage (page refresh)
        // 3. Saved quiz state exists (resume scenario)
        const hasValidAccess = sessionToken || storedToken || savedState;

        if (!hasValidAccess) {
          notify({
            title: 'Invalid Access',
            message: 'Please start the quiz from the overview page.',
            type: 'error',
          });
          navigate(`/quiz/${quizSlug}/overview`, { replace: true });
          return;
        }

        const result = await getQuizBySlug(quizSlug);

        if (!result.success) {
          navigate('/main', { replace: true });
          return;
        }

        const quizData = result.data;
        const isOwner = auth.currentUser && quizData.createdBy === auth.currentUser.uid;

        if (!quizData.isPublished && !isOwner) {
          notify({
            title: 'Quiz Not Available',
            message: 'This quiz is not published yet.',
            type: 'error',
          });
          navigate('/main', { replace: true });
          return;
        }

        // Shuffle questions if enabled
        if (quizData.settings?.shuffleQuestions) {
          quizData.questions = [...quizData.questions].sort(() => Math.random() - 0.5);
        }

        // Randomize options
        if (quizData.randomizeOptions) {
          quizData.questions = quizData.questions.map(question => {
            if (question.type === 'multiple-choice' && question.options) {
              return {
                ...question,
                options: [...question.options].sort(() => Math.random() - 0.5)
              };
            }
            return question;
          });
        }

        // CRITICAL FIX: Assign stable IDs to questions/options if undefined
        quizData.questions = quizData.questions.map((q, qIdx) => ({
          ...q,
          id: q.id !== undefined ? q.id : `q_${qIdx}`,
          options: q.options?.map((opt, optIdx) => ({
            ...opt,
            id: opt.id !== undefined ? opt.id : `q${qIdx}_opt${optIdx}`
          }))
        }));

        setQuiz(quizData);

        // Check URL params first (refresh scenario)
        const urlQuestionIndex = parseInt(searchParams.get('q')) || 0;
        const urlAnswersParam = searchParams.get('a');
        const urlFlagsParam = searchParams.get('f');
        const urlTimeParam = searchParams.get('t');

        if (urlAnswersParam || urlQuestionIndex > 0) {
          // AUTO-RESUME from URL params (SECURE DECODING)
          try {
            const decodedAnswers = urlAnswersParam ? decodeQuizState(urlAnswersParam) || {} : {};
            const decodedFlags = urlFlagsParam ? decodeArray(urlFlagsParam) : [];
            const remainingTime = urlTimeParam ? parseInt(urlTimeParam) : null;

            setUserAnswers(decodedAnswers);
            setFlaggedQuestions(new Set(decodedFlags));
            setCurrentQuestionIndex(Math.min(urlQuestionIndex, quizData.questions.length - 1));

            if (remainingTime && quizData.settings?.timeLimit) {
              setTimeRemaining(remainingTime);
              const elapsedSeconds = (quizData.settings.timeLimit * 60) - remainingTime;
              startTimeRef.current = Date.now() - (elapsedSeconds * 1000);
            } else if (quizData.settings?.timeLimit && quizData.settings.timeLimit > 0) {
              setTimeRemaining(quizData.settings.timeLimit * 60);
              startTimeRef.current = Date.now();
            } else {
              // For untimed quizzes, still track start time
              startTimeRef.current = Date.now();
            }

            const newAttemptId = `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            setAttemptId(newAttemptId);
          } catch (e) {
            console.error('Failed to parse URL params:', e);
            initializeFreshQuiz(quizData);
          }
        } else if (savedState) {
          // Fallback to localStorage
          const parsed = JSON.parse(savedState);
          const hasProgress = Object.keys(parsed.userAnswers || {}).length > 0;

          if (hasProgress) {
            setShowResumeDialog(true);
            window.__savedQuizState = parsed;
          } else {
            initializeFreshQuiz(quizData);
          }
        } else {
          // No saved state - start fresh
          initializeFreshQuiz(quizData);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading quiz:', error);
        notify({
          title: 'Error',
          message: 'Failed to load quiz',
          type: 'error',
        });
        navigate('/main', { replace: true });
      }
    };

    loadQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizSlug]);

  const initializeFreshQuiz = (quizData) => {
    // Clear all previous state
    setUserAnswers({});
    setFlaggedQuestions(new Set());
    setCurrentQuestionIndex(0);

    if (quizData.settings?.timeLimit && quizData.settings.timeLimit > 0) {
      setTimeRemaining(quizData.settings.timeLimit * 60);
      startTimeRef.current = Date.now();
    } else {
      // For untimed quizzes, still track start time
      startTimeRef.current = Date.now();
    }
    // Generate new attempt ID
    const newAttemptId = `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setAttemptId(newAttemptId);
  };

  const handleResumeChoice = (shouldResume) => {
    setShowResumeDialog(false);

    if (shouldResume && window.__savedQuizState) {
      const parsed = window.__savedQuizState;
      setUserAnswers(parsed.userAnswers || {});
      setFlaggedQuestions(new Set(parsed.flaggedQuestions || []));
      setCurrentQuestionIndex(parsed.currentQuestionIndex || 0);

      if (parsed.timeRemaining !== null && parsed.timeRemaining !== undefined) {
        setTimeRemaining(parsed.timeRemaining);
        startTimeRef.current = Date.now() - ((quiz.settings?.timeLimit * 60 - parsed.timeRemaining) * 1000);
      } else if (quiz.settings?.timeLimit && quiz.settings.timeLimit > 0) {
        setTimeRemaining(quiz.settings.timeLimit * 60);
        startTimeRef.current = Date.now();
      }

      if (parsed.attemptId) {
        setAttemptId(parsed.attemptId);
      }
    } else {
      // Start fresh - clear localStorage
      localStorage.removeItem(storageKey);
      initializeFreshQuiz(quiz);
    }

    delete window.__savedQuizState;
  };


  // Calculate score
  const calculateScore = useCallback(() => {
    if (!quiz) return { totalScore: 0, maxScore: 0, percentage: 0, questionResults: [] };

    let totalScore = 0;
    const questionResults = [];

    quiz.questions.forEach((question) => {
      const userAnswer = userAnswers[question.id];
      let isCorrect = false;
      let pointsEarned = 0;

      if (question.type === 'multiple-choice') {
        if (!question.allowMultipleAnswers) {
          const correctOption = question.options.find(opt => opt.isCorrect);
          isCorrect = userAnswer === correctOption?.id;
          pointsEarned = isCorrect ? question.points : 0;
        } else {
          const selectedIds = Array.isArray(userAnswer) ? userAnswer : [];
          pointsEarned = calculateMultipleChoiceScore(question, selectedIds);
          isCorrect = pointsEarned === question.points;
        }
      } else if (question.type === 'short-answer') {
        const correctAnswers = question.correctAnswers || [];
        const userText = (userAnswer || '').trim();

        isCorrect = correctAnswers.some(ans => {
          const answerText = ans.value.trim();
          if (question.matchingMode === 'exact-case-sensitive') {
            return userText === answerText;
          } else if (question.matchingMode === 'contains') {
            return userText.toLowerCase().includes(answerText.toLowerCase());
          } else {
            return userText.toLowerCase() === answerText.toLowerCase();
          }
        });
        pointsEarned = isCorrect ? question.points : 0;
      } else if (question.type === 'true-false') {
        isCorrect = userAnswer === question.correctAnswer;
        pointsEarned = isCorrect ? question.points : 0;
      }

      totalScore += pointsEarned;
      questionResults.push({
        questionId: question.id,
        isCorrect,
        pointsEarned,
        maxPoints: question.points,
        userAnswer
      });
    });

    const maxScore = quiz.questions.reduce((sum, q) => sum + q.points, 0);
    const percentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0;

    return { totalScore, maxScore, percentage, questionResults };
  }, [quiz, userAnswers]);

  // Submit quiz
  const submitQuiz = useCallback(async (forceSubmit = false) => {
    // If not forcing submit, show appropriate confirmation modal
    if (!forceSubmit) {
      const unanswered = quiz.questions.length - answeredCount;
      const navigationType = quiz.settings?.navigationType || 'free';

      // Only show unanswered warning in free navigation mode
      // In sequential mode, users already got warned when skipping questions
      if (unanswered > 0 && navigationType === 'free') {
        setShowUnansweredWarning(true);
        return;
      }

      // All answered (or sequential mode), show regular submit confirmation
      setShowSubmitModal(true);
      return;
    }

    // Actually submit the quiz
    setSubmitting(true);
    quizActiveRef.current = false;
    submissionStartedRef.current = true; // Mark submission as started to prevent security check

    // Clear quiz session token and saved state
    sessionStorage.removeItem(`quiz_session_${quizSlug}`);
    localStorage.removeItem(storageKey);

    try {
      const scoreData = calculateScore();
      const timeSpent = startTimeRef.current
        ? Math.floor((Date.now() - startTimeRef.current) / 1000)
        : null;

      const attemptData = {
        quizId: quiz.id,
        userId: auth.currentUser.uid,
        userAnswers,
        score: scoreData.totalScore,
        maxScore: scoreData.maxScore,
        percentage: scoreData.percentage,
        timeSpent,
        questionResults: scoreData.questionResults,
        completedAt: new Date().toISOString(),
      };

      const attemptResult = await createQuizAttempt(attemptData);

      if (attemptResult.success) {
        await updateQuiz(quiz.id, {
          'stats.attempts': increment(1),
          'stats.totalScore': increment(scoreData.totalScore),
        });

        localStorage.removeItem(storageKey);

        const passingScore = quiz.settings?.passingScore || 50;
        const hasPassingScore = quiz.settings?.hasPassingScore !== false; // Default to true if not explicitly set
        const hasPassed = scoreData.percentage >= passingScore;

        setResultsData({
          attemptId: attemptResult.id,
          scoreData,
          quiz,
          userAnswers,
          timeSpent,
          hasPassed,
          passingScore: hasPassingScore ? passingScore : null
        });

        // Close any open modals and show results
        // For timeout, delay showing results to let user read the message
        if (showTimeoutModal) {
          setTimeout(() => {
            setShowTimeoutModal(false);
            setShowResultsModal(true);
          }, 2000);
        } else {
          // For normal submit, show results immediately
          setShowUnansweredWarning(false);
          setShowSubmitModal(false);
          setShowResultsModal(true);
        }
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      notify({
        title: 'Error',
        message: 'Failed to submit quiz',
        type: 'error',
      });
      setShowSubmitModal(false);
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quiz, userAnswers, storageKey, calculateScore, notify]);

  // Handle timeout - Soft lock with modal, then submit
  const handleTimeout = useCallback(() => {
    setShowTimeoutModal(true);
    // Submit immediately but show modal overlay during the process
    submitQuiz(true);
  }, [submitQuiz]);

  // Timer countdown for timed quizzes
  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timeRemaining, handleTimeout]);

  // Elapsed time counter for untimed quizzes
  useEffect(() => {
    // Only run for untimed quizzes (when timeRemaining is null and quiz is loaded)
    if (timeRemaining !== null || !quiz) return;

    console.log('Starting elapsed time counter for untimed quiz', { startTime: startTimeRef.current });

    const intervalId = setInterval(() => {
      if (startTimeRef.current) {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedTime(elapsed);
      }
    }, 1000);

    return () => {
      console.log('Cleaning up elapsed time counter');
      clearInterval(intervalId);
    };
  }, [timeRemaining, quiz]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Save state to localStorage AND URL params
  useEffect(() => {
    if (quiz && quizActiveRef.current) {
      const stateToSave = {
        userAnswers,
        flaggedQuestions: Array.from(flaggedQuestions),
        currentQuestionIndex,
        timeRemaining,
        attemptId,
        timestamp: Date.now()
      };
      localStorage.setItem(storageKey, JSON.stringify(stateToSave));

      // Update URL params for refresh persistence (SECURE ENCODING)
      const params = new URLSearchParams();
      params.set('q', currentQuestionIndex.toString());
      if (Object.keys(userAnswers).length > 0) {
        params.set('a', encodeQuizState(userAnswers));
      }
      if (flaggedQuestions.size > 0) {
        params.set('f', encodeArray(Array.from(flaggedQuestions)));
      }
      if (timeRemaining !== null) {
        params.set('t', timeRemaining.toString());
      }
      setSearchParams(params, { replace: true });
    }
  }, [userAnswers, flaggedQuestions, currentQuestionIndex, timeRemaining, attemptId, quiz, storageKey, setSearchParams]);

  // Handle answer change
  const handleAnswerChange = (questionId, answer) => {
    console.log('💾 handleAnswerChange:', { questionId, answer });
    setUserAnswers(prev => {
      const updated = {
        ...prev,
        [questionId]: answer
      };
      console.log('💾 Updated userAnswers:', updated);
      return updated;
    });
  };

  // Toggle flag
  const toggleFlag = (questionId) => {
    console.log('🚩 Toggle flag called for question ID:', questionId);
    console.log('📋 All question IDs:', quiz?.questions?.map(q => q.id));
    console.log('🏴 Currently flagged:', Array.from(flaggedQuestions));

    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        console.log('➖ Removing flag from:', questionId);
        newSet.delete(questionId);
      } else {
        console.log('➕ Adding flag to:', questionId);
        newSet.add(questionId);
      }
      console.log('🏴 New flagged set:', Array.from(newSet));
      return newSet;
    });
  };

  // Navigation
  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    setShowNavigator(false);
    setHintExpanded(false);
  };

  const goToPrevious = () => {
    if (currentQuestionIndex > 0) {
      const newIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(newIndex);
      setHintExpanded(false);
    }
  };

  const goToNext = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      // In sequential mode, warn if current question is unanswered
      const navigationType = quiz.settings?.navigationType || 'free';
      const currentQ = quiz.questions[currentQuestionIndex];
      const answer = userAnswers[currentQ.id];

      const isAnswered = answer !== undefined && answer !== null && (
        Array.isArray(answer) ? answer.length > 0 :
        typeof answer === 'string' ? answer.trim().length > 0 :
        true
      );

      if (navigationType === 'sequential' && !isAnswered) {
        setShowSkipWarning(true);
        return;
      }

      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      setHintExpanded(false);
    }
  };

  // Count answered questions
  const answeredCount = React.useMemo(() => {
    if (!quiz?.questions) return 0;
    return quiz.questions.filter(q => {
      const answer = userAnswers[q.id];
      if (answer === undefined || answer === null) return false;
      if (Array.isArray(answer)) return answer.length > 0;
      if (typeof answer === 'string') return answer.trim().length > 0;
      if (typeof answer === 'boolean') return true;
      return true;
    }).length;
  }, [quiz, userAnswers]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-slate-900 text-xl font-bold mb-2">Loading Quiz</p>
          <p className="text-slate-600 text-sm">Preparing your experience...</p>
        </div>
      </div>
    );
  }

  if (!quiz) return null;

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const progress = Math.round((answeredCount / quiz.questions.length) * 100);
  const isLastQuestion = currentQuestionIndex === quiz.questions.length - 1;
  const navigationType = quiz.settings?.navigationType || 'free';
  const isSequentialMode = navigationType === 'sequential';

  return (
    <>
      {/* Custom Scrollbar Styles */}
      <style>{`
        /* Quiz content scrollbar */
        .quiz-content-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .quiz-content-scroll::-webkit-scrollbar-track {
          background: #f8fafc;
          border-radius: 10px;
        }
        .quiz-content-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .quiz-content-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Question navigator scrollbar */
        .quiz-navigator-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .quiz-navigator-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 8px;
        }
        .quiz-navigator-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 8px;
        }
        .quiz-navigator-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>

      {/* IMMERSIVE FULL-SCREEN QUIZ CANVAS */}
      <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-hidden">

        {/* TOP COMMAND BAR - COMPACT */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-4 md:px-6 py-2.5">
            {/* Left: Exit Button + Progress */}
            <div className="flex items-center gap-3 flex-1">
              {/* Minimalist Exit Button */}
              <button
                onClick={() => setShowExitModal(true)}
                className="text-sm text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-1"
                title="Exit quiz"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden md:inline">Exit</span>
              </button>

              <div className="h-4 w-px bg-slate-300"></div>
              <span className="text-xs font-semibold text-slate-700">
                {answeredCount} of {quiz.questions.length} Answered
              </span>
              <div className="h-1.5 w-20 md:w-28 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs font-bold text-emerald-600">{progress}%</span>
            </div>

            {/* Center: Quiz Title */}
            <div className="hidden lg:block text-center flex-1">
              <h2 className="text-sm font-bold text-slate-900 truncate">{quiz.title}</h2>
              <p className="text-xs text-slate-500">Q{currentQuestionIndex + 1}/{quiz.questions.length}</p>
            </div>

            {/* Right: Timer + Menu */}
            <div className="flex items-center gap-2 flex-1 justify-end">
              {!isSequentialMode && (
                <button
                  onClick={() => setShowNavigator(true)}
                  className="lg:hidden p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                  title="View all questions"
                >
                  <Menu className="w-4 h-4 text-slate-600" />
                </button>
              )}

              {/* Timer - show countdown for timed quizzes, elapsed time for untimed */}
              {timeRemaining !== null ? (
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-xs transition-all ${
                  timeRemaining < 60
                    ? 'bg-red-600 text-white animate-pulse shadow-lg'
                    : timeRemaining < 300
                    ? 'bg-amber-500 text-white shadow-md'
                    : 'bg-emerald-600 text-white'
                }`}>
                  <Clock className={`w-3.5 h-3.5 ${timeRemaining < 60 ? 'animate-bounce' : ''}`} />
                  <span className="tabular-nums">{formatTime(timeRemaining)}</span>
                  {timeRemaining < 60 && <span className="hidden sm:inline text-[10px] uppercase">Hurry!</span>}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-xs bg-slate-600 text-white">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="tabular-nums">{formatTime(elapsedTime)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* MAIN CONTENT AREA */}
        <div className="flex h-full pt-14 pb-4">

          {/* LEFT SIDEBAR - COMPACT (Free Mode Only, Desktop) */}
          {!isSequentialMode && (
            <aside className="hidden lg:block w-52 bg-white border-r border-slate-200 overflow-y-auto flex-shrink-0 quiz-navigator-scroll">
              <div className="p-4">
                <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">Questions</h3>
                <div className="grid grid-cols-4 gap-2">
                  {quiz.questions.map((question, index) => {
                    const isCurrent = index === currentQuestionIndex;
                    const answer = userAnswers[question.id];
                    const isAnswered = answer !== undefined && answer !== null && (
                      Array.isArray(answer) ? answer.length > 0 :
                      typeof answer === 'string' ? answer.trim().length > 0 :
                      typeof answer === 'boolean' ? true : true
                    );
                    const isFlagged = flaggedQuestions.has(question.id);

                    return (
                      <button
                        key={`nav-q-${question.id || index}`}
                        onClick={() => goToQuestion(index)}
                        className={`relative aspect-square flex items-center justify-center rounded-lg font-bold text-xs transition-all duration-200 ${
                          isCurrent
                            ? 'bg-emerald-600 text-white ring-2 ring-emerald-400 scale-105 shadow-md'
                            : isAnswered
                            ? 'bg-slate-100 text-slate-700 border border-slate-300'
                            : 'bg-white border border-slate-200 text-slate-500 hover:border-slate-300'
                        }`}
                      >
                        {index + 1}
                        {isFlagged && (
                          <div className="absolute -top-0.5 -right-0.5 bg-indigo-100 border border-indigo-300 rounded-full p-0.5 shadow-sm">
                            <Flag className="w-2 h-2 text-indigo-600" fill="currentColor" strokeWidth={0} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>
          )}

          {/* RIGHT: Question Canvas */}
          <main className="flex-1 overflow-y-auto px-4 md:px-6 py-4 quiz-content-scroll">
            <div className="max-w-4xl mx-auto">

              {/* Question Card with INTEGRATED NAVIGATION */}
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

                {/* Question Header - COMPACT */}
                <div className="bg-gradient-to-r from-slate-50 to-white px-5 py-3 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                        Question {currentQuestionIndex + 1}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                        {currentQuestion.points} pt{currentQuestion.points !== 1 ? 's' : ''}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {currentQuestion.type === 'multiple-choice'
                          ? currentQuestion.allowMultipleAnswers ? 'Multiple Choice' : 'Single Choice'
                          : currentQuestion.type === 'short-answer' ? 'Short Answer'
                          : 'True/False'}
                      </span>
                    </div>

                    {/* Flag Button - SOFT INDIGO OUTLINE */}
                    <button
                      onClick={() => toggleFlag(currentQuestion.id)}
                      title={flaggedQuestions.has(currentQuestion.id) ? 'Unflag question' : 'Flag for review'}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        flaggedQuestions.has(currentQuestion.id)
                          ? 'bg-indigo-50 border border-indigo-200 text-indigo-600 hover:bg-indigo-100'
                          : 'bg-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Flag className="w-4 h-4" fill={flaggedQuestions.has(currentQuestion.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} />
                    </button>
                  </div>
                </div>

                {/* Question Body - Overlapping Media Design */}
                {currentQuestion.imageUrl ? (
                  /* OVERLAPPING MEDIA CARD LAYOUT */
                  <div className="flex flex-col">
                    {/* Top Section: Media Layer */}
                    <div className="relative w-full max-h-[400px] min-h-[280px] bg-slate-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={currentQuestion.imageUrl}
                        alt={currentQuestion.imageCaption || "Question visual"}
                        className="w-full h-full object-contain max-h-[400px]"
                        loading="lazy"
                      />

                      {/* Image Caption Overlay */}
                      {currentQuestion.imageCaption && (
                        <div className="absolute bottom-4 left-4 right-4 z-20 bg-slate-900/75 backdrop-blur-sm text-white text-xs px-3 py-2 rounded-lg max-w-full line-clamp-2">
                          {currentQuestion.imageCaption}
                        </div>
                      )}
                    </div>

                    {/* Bottom Section: Content Layer with Overlap */}
                    <div className="bg-white text-slate-900 px-6 pt-8 pb-6 -mt-6 relative z-10 rounded-t-3xl">
                      {/* Question Text */}
                      <div className="text-lg md:text-xl text-slate-900 font-semibold mb-5 leading-relaxed">
                        <SafeMathText text={currentQuestion.questionText || currentQuestion.text || currentQuestion.question || ''} />
                      </div>

                      {/* Hint Section */}
                      {currentQuestion.hint && (
                        <div className="mb-5">
                          <button
                            onClick={() => setHintExpanded(!hintExpanded)}
                            className="w-full flex items-center justify-between p-3 bg-amber-50 border-2 border-amber-200 rounded-xl hover:bg-amber-100 transition-all group"
                          >
                            <div className="flex items-center gap-2">
                              <Lightbulb className="w-4 h-4 text-amber-600" />
                              <span className="font-semibold text-sm text-amber-900">💡 View Hint</span>
                            </div>
                            <ChevronDown className={`w-3.5 h-3.5 text-amber-600 transition-transform ${hintExpanded ? 'rotate-180' : ''}`} />
                          </button>

                          {hintExpanded && (
                            <div className="mt-2 p-3 bg-white border-2 border-amber-200 rounded-xl animate-in slide-in-from-top-2 duration-300 text-sm">
                              <SafeMathText text={currentQuestion.hint} />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Answer Canvas */}
                      <div>
                        <QuestionAnswerCanvas
                          question={currentQuestion}
                          userAnswer={userAnswers[currentQuestion.id]}
                          onAnswerChange={(answer) => handleAnswerChange(currentQuestion.id, answer)}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* STANDARD LAYOUT (NO IMAGE) */
                  <div className="p-5 md:p-6">
                    {/* Question Text */}
                    <div className="text-lg md:text-xl text-slate-900 font-semibold mb-5 leading-relaxed">
                      <SafeMathText text={currentQuestion.questionText || currentQuestion.text || currentQuestion.question || ''} />
                    </div>

                    {/* Hint Section */}
                    {currentQuestion.hint && (
                      <div className="mb-5">
                        <button
                          onClick={() => setHintExpanded(!hintExpanded)}
                          className="w-full flex items-center justify-between p-3 bg-amber-50 border-2 border-amber-200 rounded-xl hover:bg-amber-100 transition-all group"
                        >
                          <div className="flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-amber-600" />
                            <span className="font-semibold text-sm text-amber-900">💡 View Hint</span>
                          </div>
                          <ChevronDown className={`w-3.5 h-3.5 text-amber-600 transition-transform ${hintExpanded ? 'rotate-180' : ''}`} />
                        </button>

                        {hintExpanded && (
                          <div className="mt-2 p-3 bg-white border-2 border-amber-200 rounded-xl animate-in slide-in-from-top-2 duration-300 text-sm">
                            <SafeMathText text={currentQuestion.hint} />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Answer Canvas */}
                    <div>
                      <QuestionAnswerCanvas
                        question={currentQuestion}
                        userAnswer={userAnswers[currentQuestion.id]}
                        onAnswerChange={(answer) => handleAnswerChange(currentQuestion.id, answer)}
                      />
                    </div>
                  </div>
                )}

                {/* INTEGRATED NAVIGATION FOOTER - SMOOTH DESIGN */}
                <div className="bg-white px-6 py-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    {/* Previous Button */}
                    {!isSequentialMode && currentQuestionIndex > 0 ? (
                      <button
                        onClick={goToPrevious}
                        className="group px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 rounded-xl text-sm font-semibold transition-all duration-200 hover:shadow-sm active:scale-[0.98]"
                      >
                        <div className="flex items-center gap-2">
                          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                          <span>Prev</span>
                        </div>
                      </button>
                    ) : (
                      <div className="w-24"></div>
                    )}

                    {/* Progress Indicator (Mobile) */}
                    <div className="lg:hidden flex items-center gap-2">
                      <div className="text-xs font-semibold text-slate-500">
                        {currentQuestionIndex + 1}
                      </div>
                      <div className="w-8 h-1 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 transition-all duration-300"
                          style={{ width: `${((currentQuestionIndex + 1) / quiz.questions.length) * 100}%` }}
                        />
                      </div>
                      <div className="text-xs font-semibold text-slate-400">
                        {quiz.questions.length}
                      </div>
                    </div>

                    {/* Next / Submit Button */}
                    {!isLastQuestion ? (
                      <button
                        onClick={goToNext}
                        className="group px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold transition-all duration-200 hover:shadow-lg active:scale-[0.98]"
                      >
                        <div className="flex items-center gap-2">
                          <span>Next</span>
                          <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      </button>
                    ) : (
                      <button
                        onClick={() => submitQuiz(false)}
                        className="group px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-xl text-sm font-semibold transition-all duration-200 hover:shadow-lg active:scale-[0.98]"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          <span>Submit</span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </main>
        </div>

        {/* MODALS */}
        {showResumeDialog && (
          <ResumeDialog onChoice={handleResumeChoice} />
        )}

        {showUnansweredWarning && (
          <UnansweredWarningModal
            unansweredCount={quiz.questions.length - answeredCount}
            onContinue={() => {
              // Submit directly (force submit with unanswered questions)
              submitQuiz(true);
            }}
            onCancel={() => {
              setShowUnansweredWarning(false);
            }}
          />
        )}

        {showSkipWarning && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(8px)' }}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-xs w-full overflow-hidden animate-in zoom-in-95 fade-in duration-300">

              {/* Icon + Title Section - Centered, Clean */}
              <div className="pt-6 pb-4 px-5 text-center">
                {/* Icon with subtle pulse animation */}
                <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-100 rounded-xl mb-3 animate-pulse">
                  <AlertCircle className="w-6 h-6 text-amber-600" strokeWidth={2.5} />
                </div>

                {/* Title - Bold, Clear */}
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Question Not Answered
                </h3>

                {/* Body text - Concise, readable */}
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  Skipping this question will mark it as incorrect. Take a moment to answer before moving forward.
                </p>

                {/* Subtle tip - minimal design */}
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 rounded-full">
                  <span className="text-amber-600 text-xs">💡</span>
                  <span className="text-xs text-amber-800 font-medium">Review carefully to maximize your score</span>
                </div>
              </div>

              {/* Action Buttons - Vertical stack for better mobile UX */}
              <div className="px-5 pb-5 space-y-2">
                {/* Primary action - Emerald green (brand color) */}
                <button
                  onClick={() => setShowSkipWarning(false)}
                  className="w-full px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold shadow-lg shadow-emerald-600/20 hover:shadow-xl hover:shadow-emerald-600/30 transition-all active:scale-[0.98]"
                >
                  Answer Now
                </button>

                {/* Secondary action - Low emphasis */}
                <button
                  onClick={() => {
                    setShowSkipWarning(false);
                    const newIndex = currentQuestionIndex + 1;
                    setCurrentQuestionIndex(newIndex);
                    setHintExpanded(false);
                  }}
                  className="w-full px-5 py-2 text-slate-600 hover:text-slate-900 text-xs font-medium transition-colors"
                >
                  Skip Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {showExitModal && (
          <ExitConfirmationModal
            onConfirm={() => {
              quizActiveRef.current = false; // Disable exit warning
              // Clear quiz session token
              sessionStorage.removeItem(`quiz_session_${quizSlug}`);
              localStorage.removeItem(storageKey);
              navigate('/main', { replace: true });
            }}
            onCancel={() => setShowExitModal(false)}
          />
        )}

        {showNavigator && (
          <QuestionNavigatorModal
            questions={quiz.questions}
            currentIndex={currentQuestionIndex}
            userAnswers={userAnswers}
            flaggedQuestions={flaggedQuestions}
            onNavigate={goToQuestion}
            onClose={() => setShowNavigator(false)}
          />
        )}

        {showSubmitModal && (
          <SubmitConfirmationModal
            answeredCount={answeredCount}
            totalQuestions={quiz.questions.length}
            flaggedCount={flaggedQuestions.size}
            onConfirm={() => submitQuiz(true)}
            onCancel={() => setShowSubmitModal(false)}
            submitting={submitting}
          />
        )}

        {showTimeoutModal && (
          <TimeoutModal quiz={quiz} />
        )}

        {showResultsModal && resultsData && !isNavigatingToReview && (
          <ResultsModal
            resultsData={resultsData}
            onClose={() => {
              quizActiveRef.current = false;
              setShowResultsModal(false);
              // Small delay for smooth close animation
              setTimeout(() => {
                navigate('/main', { replace: true });
              }, 200);
            }}
            onReview={() => {
              console.log('onReview called', { quiz: resultsData.quiz, quizSlug });
              quizActiveRef.current = false;

              // Hide modal and show loading screen
              setShowResultsModal(false);
              setIsNavigatingToReview(true);

              // Use slug from resultsData.quiz to ensure it's available
              const quizSlugToUse = resultsData.quiz?.slug || quizSlug;
              console.log('Navigating to review:', `/quiz/${quizSlugToUse}/review`);

              // Navigate after smooth transition
              setTimeout(() => {
                console.log('Executing navigation to review');
                navigate(`/quiz/${quizSlugToUse}/review`, {
                  state: resultsData,
                  replace: true
                });
              }, 500);
            }}
          />
        )}

        {/* Loading Screen Transition - Smooth navigation to review */}
        {isNavigatingToReview && (
          <div className="fixed inset-0 z-[60] bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100 flex items-center justify-center animate-in fade-in duration-300">
            <div className="text-center">
              {/* Checkmark Icon */}
              <div className="relative inline-flex mb-6">
                <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center shadow-2xl animate-in zoom-in-95 duration-500">
                  <CheckCircle className="w-12 h-12 text-white" strokeWidth={2.5} />
                </div>
                {/* Subtle pulse rings */}
                <div className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping"></div>
                <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-pulse"></div>
              </div>

              {/* Loading Text */}
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                Loading Your Results
              </h3>
              <p className="text-sm text-slate-600 mb-6">
                Preparing your answer review...
              </p>

              {/* Animated Progress Bar */}
              <div className="w-48 h-1.5 bg-emerald-200 rounded-full overflow-hidden mx-auto">
                <div className="h-full bg-emerald-600 rounded-full animate-[shimmer_1s_ease-in-out_infinite]" style={{ width: '40%' }}></div>
              </div>
            </div>

            {/* Add shimmer animation */}
            <style>{`
              @keyframes shimmer {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(350%); }
              }
            `}</style>
          </div>
        )}
      </div>
    </>
  );
};

// Resume Dialog Component
const ResumeDialog = ({ onChoice }) => {
  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
        <div className="w-16 h-16 bg-emerald-100 rounded-full mx-auto mb-6 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 text-emerald-600" />
        </div>

        <h2 className="text-2xl font-bold text-slate-900 mb-3 text-center">Resume Quiz?</h2>
        <p className="text-slate-600 text-center mb-6">
          We found a previous attempt. Would you like to continue where you left off or start fresh?
        </p>

        <div className="flex gap-3">
          <button
            onClick={() => onChoice(false)}
            className="flex-1 px-6 py-3 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50"
          >
            Start Fresh
          </button>
          <button
            onClick={() => onChoice(true)}
            className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

const ExitConfirmationModal = ({ onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-md">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Red Warning Header */}
        <div className="bg-gradient-to-r from-red-50 to-pink-50 px-6 pt-6 pb-5 border-b border-red-200">
          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
            <AlertCircle className="w-12 h-12 text-red-500" strokeWidth={2.5} />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-1 text-center">Exit Quiz?</h2>
          <p className="text-xs text-red-600 text-center font-medium">
            This action cannot be undone
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Clear Warning Message */}
          <p className="text-sm text-slate-700 leading-relaxed mb-6 text-center">
            Leaving now will immediately end your session and submit your current progress, resulting in a <span className="font-bold text-slate-900">score of 0</span> for this attempt.
          </p>

          {/* Action Buttons - Inline */}
          <div className="flex flex-row gap-3 w-full">
            {/* Secondary: Exit (Left) */}
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2.5 bg-slate-100 border border-slate-300 text-slate-600 hover:bg-slate-200 rounded-lg font-semibold transition-all"
            >
              Exit
            </button>

            {/* Primary: Stay (Right) */}
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
            >
              Stay
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Question Answer Canvas Component
const QuestionAnswerCanvas = ({ question, userAnswer, onAnswerChange }) => {
  switch (question.type) {
    case 'multiple-choice':
      return <MultipleChoiceAnswer question={question} userAnswer={userAnswer} onAnswerChange={onAnswerChange} />;
    case 'short-answer':
      return <ShortAnswerInput userAnswer={userAnswer} onAnswerChange={onAnswerChange} />;
    case 'true-false':
      return <TrueFalseAnswer userAnswer={userAnswer} onAnswerChange={onAnswerChange} />;
    default:
      return <div className="text-red-600">Unsupported question type</div>;
  }
};

// Multiple Choice Answer Component - HIGH CONTRAST DESIGNER SPEC v2.0
// CACHE BUSTER: 2026-06-22-v2
const MultipleChoiceAnswer = ({ question, userAnswer, onAnswerChange }) => {
  const isSingleChoice = !question.allowMultipleAnswers;

  const handleOptionClick = (optionId) => {
    console.log('🖱️ OPTION CLICKED:', optionId);
    console.log('📝 Current userAnswer:', userAnswer);
    console.log('🔄 onAnswerChange function:', typeof onAnswerChange);

    if (isSingleChoice) {
      // Single choice: Replace with clicked option
      console.log('✅ Setting single choice to:', optionId);
      onAnswerChange(optionId);
    } else {
      // Multiple choice: Toggle in array
      const current = Array.isArray(userAnswer) ? userAnswer : [];
      if (current.includes(optionId)) {
        console.log('➖ Removing from array');
        onAnswerChange(current.filter(id => id !== optionId));
      } else {
        console.log('➕ Adding to array');
        onAnswerChange([...current, optionId]);
      }
    }
  };

  const isOptionSelected = (optionId) => {
    // Defensive: Ensure we have valid data
    if (optionId === undefined || optionId === null) return false;
    if (userAnswer === undefined || userAnswer === null) return false;

    if (isSingleChoice) {
      // Single choice: Strict equality check
      return userAnswer === optionId;
    } else {
      // Multiple choice: Array membership
      return Array.isArray(userAnswer) && userAnswer.includes(optionId);
    }
  };

  return (
    <div className="space-y-3">
      {/* Multiple Choice Banner */}
      {!isSingleChoice && (
        <div className="px-3 py-2.5 bg-blue-50/80 border border-blue-200/80 rounded-lg mb-3 backdrop-blur-sm">
          <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Select all that apply
          </p>
        </div>
      )}

      {/* Options List */}
      {question.options?.map((option, idx) => {
        const isSelected = isOptionSelected(option.id);
        const letter = String.fromCharCode(65 + idx); // A, B, C, D...

        return (
          <div
            key={`opt-${question.id || 'q'}-${option.id || idx}-${idx}`}
            onClick={() => handleOptionClick(option.id)}
            className={`
              flex items-center p-5 rounded-xl cursor-pointer transition-all duration-150
              ${isSelected
                ? 'border-2 border-emerald-500 bg-emerald-100 text-slate-900 shadow-md'
                : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }
            `}
          >
            {/* Radio/Checkbox Element */}
            <div className={`
              w-5 h-5 flex items-center justify-center border-2 mr-4 transition-all
              ${isSingleChoice ? 'rounded-full' : 'rounded-md'}
              ${isSelected
                ? 'bg-emerald-600 border-emerald-600 text-white'
                : 'border-slate-300 bg-transparent'
              }
            `}>
              {isSelected && !isSingleChoice && (
                // Checkbox: white checkmark icon on green background
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>

            {/* Option Label and Text */}
            <div className="flex-1 flex items-start gap-2.5">
              {/* Letter Label */}
              <span className={`text-sm font-bold ${isSelected ? 'text-emerald-700' : 'text-slate-600'}`}>
                {letter}.
              </span>

              {/* Option Text */}
              <span className={`text-[15px] font-medium ${isSelected ? 'text-slate-900' : 'text-slate-700'}`}>
                <SafeMathText text={option.text || option.value || ''} />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Short Answer Input
const ShortAnswerInput = ({ userAnswer, onAnswerChange }) => {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">Your Answer</label>
      <textarea
        value={userAnswer || ''}
        onChange={(e) => onAnswerChange(e.target.value)}
        placeholder="Type your answer here..."
        rows={5}
        maxLength={200}
        className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 resize-none text-sm"
      />
    </div>
  );
};

// True/False Answer - Simple vertical layout matching multiple choice style
const TrueFalseAnswer = ({ userAnswer, onAnswerChange }) => {
  const options = [
    { value: true, label: 'True' },
    { value: false, label: 'False' }
  ];

  return (
    <div className="space-y-3">
      {options.map((option) => {
        const isSelected = userAnswer === option.value;

        return (
          <button
            key={String(option.value)}
            type="button"
            onClick={() => onAnswerChange(option.value)}
            className={`w-full flex items-center p-4 rounded-xl border-2 transition-all text-left ${
              isSelected
                ? 'border-emerald-600 bg-emerald-600'
                : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {/* Radio circle indicator */}
            <div className={`
              w-5 h-5 flex items-center justify-center border-2 rounded-full mr-4 transition-all flex-shrink-0
              ${isSelected
                ? 'bg-white border-white'
                : 'border-slate-300 bg-transparent'
              }
            `}>
              {isSelected && (
                <div className="w-2.5 h-2.5 bg-emerald-600 rounded-full" />
              )}
            </div>

            {/* Option text */}
            <span className={`text-base font-medium ${
              isSelected ? 'text-white' : 'text-slate-700'
            }`}>
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// Question Navigator Modal - COMPACT
const QuestionNavigatorModal = ({ questions, currentIndex, userAnswers, flaggedQuestions, onNavigate, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
          <h2 className="text-base font-bold text-slate-900">All Questions</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 80px)' }}>
          <div className="grid grid-cols-5 sm:grid-cols-8 gap-2.5">
            {questions.map((question, index) => {
              const isCurrent = index === currentIndex;
              const answer = userAnswers[question.id];
              const isAnswered = answer !== undefined && answer !== null && (
                Array.isArray(answer) ? answer.length > 0 :
                typeof answer === 'string' ? answer.trim().length > 0 :
                typeof answer === 'boolean' ? true : true
              );
              const isFlagged = flaggedQuestions.has(question.id);

              return (
                <button
                  key={question.id}
                  onClick={() => onNavigate(index)}
                  className={`relative aspect-square flex items-center justify-center rounded-lg font-bold text-sm transition-all ${
                    isCurrent
                      ? 'bg-emerald-600 text-white ring-4 ring-emerald-300 scale-110'
                      : isAnswered
                      ? 'bg-slate-100 text-slate-700 border-2 border-slate-300'
                      : 'bg-white border-2 border-slate-200 text-slate-500'
                  }`}
                >
                  {index + 1}
                  {isFlagged && (
                    <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white shadow-sm"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Submit Confirmation Modal
const SubmitConfirmationModal = ({ answeredCount, totalQuestions, flaggedCount, onConfirm, onCancel, submitting }) => {
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200">
        <div className="p-8 text-center">
          {/* Document Check Icon */}
          <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full mx-auto mb-6 flex items-center justify-center">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Header Title */}
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Ready to Submit?</h2>

          {/* Body Paragraph */}
          <p className="text-base text-slate-600 leading-relaxed mb-8">
            Please make sure you have answered all questions to the best of your ability. Once submitted, your answers will be locked and cannot be changed.
          </p>

          {/* Action Buttons Grid */}
          <div className="grid grid-cols-2 gap-3 w-full mt-6">
            {/* Left Button: Review */}
            <button
              onClick={onCancel}
              disabled={submitting}
              className="border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg py-2.5 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Review
            </button>

            {/* Right Button: Submit */}
            <button
              onClick={onConfirm}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg py-2.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Submit</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Timeout Modal
const TimeoutModal = ({ quiz }) => {
  const [saving, setSaving] = React.useState(true);

  // Determine review type
  const reviewPermission = quiz?.settings?.reviewPermission || 'full';

  // Context-aware messaging based on review permission
  const getModalContent = () => {
    switch (reviewPermission) {
      case 'full':
        return {
          title: 'Time\'s Up!',
          message: 'Your quiz time has expired. Don\'t worry—all your completed answers have been automatically saved.',
          redirect: 'Redirecting to your results breakdown...'
        };
      case 'partial':
        return {
          title: 'Time\'s Up!',
          message: 'Your quiz time has expired. All of your responses have been safely submitted.',
          redirect: 'Redirecting to your final score...'
        };
      case 'none':
        return {
          title: 'Time\'s Up!',
          message: 'This quiz has closed. Your attempt has been successfully saved and sent to your instructor.',
          redirect: 'Returning to your dashboard...'
        };
      default:
        return {
          title: 'Time\'s Up!',
          message: 'Your quiz time has expired. Don\'t worry—all your completed answers have been automatically saved.',
          redirect: 'Redirecting to your results breakdown...'
        };
    }
  };

  const { title, message, redirect } = getModalContent();

  // Show saving spinner for realistic feedback
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSaving(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-8 text-center">
          {/* Clock Icon */}
          <div className="w-20 h-20 bg-amber-50 rounded-full mx-auto mb-5 flex items-center justify-center">
            <div className="relative">
              <Clock className="w-10 h-10 text-amber-600" />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full animate-ping"></div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-slate-900 mb-4">{title}</h2>

          {/* Context-aware message */}
          <p className="text-base text-slate-700 leading-relaxed mb-2">{message}</p>

          {/* Redirect message with spinner */}
          <div className="flex items-center justify-center gap-2 text-sm text-slate-600 mt-4">
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Saving your work...</span>
              </>
            ) : (
              <>
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span>{redirect}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Results Modal
const ResultsModal = ({ resultsData, onClose, onReview }) => {
  const { scoreData, hasPassed, passingScore, quiz } = resultsData;
  const reviewPermission = quiz.settings?.reviewPermission || 'full';

  // Layout configuration based on review permission and pass/fail status
  const layoutConfig = {
    full: {
      passed: {
        icon: 'badge',
        title: 'Awesome Job! You Passed!',
        body: 'Fantastic effort! You\'ve successfully conquered this quiz and met the passing criteria.',
        buttonText: 'Review Answers',
        buttonAction: onReview,
        showStats: false,
        showScore: true
      },
      failed: {
        icon: 'book',
        title: 'Nice Try! Not Quite There Yet.',
        body: 'Don\'t sweat it, learning is a process. Review your mistakes below and let\'s crush it on the next attempt!',
        buttonText: 'Review Answers',
        buttonAction: onReview,
        showStats: false,
        showScore: true
      }
    },
    partial: {
      passed: {
        icon: 'party',
        title: 'Congratulations!',
        body: 'Your final score has been locked in. Great work on finishing this assessment!',
        buttonText: 'Back to Dashboard',
        buttonAction: onClose,
        showStats: false,
        showScore: true
      },
      failed: {
        icon: 'thumbs',
        title: 'Quiz Completed',
        body: 'Your attempt has been submitted successfully. Keep studying, you\'ll get it next time!',
        buttonText: 'Back to Dashboard',
        buttonAction: onClose,
        showStats: false,
        showScore: true
      }
    },
    none: {
      icon: 'checkmark',
      title: 'Quiz Submitted Successfully!',
      body: 'Your responses have been securely saved. Your instructor will review your submission.',
      buttonText: 'Back to Dashboard',
      buttonAction: onClose,
      showStats: false,
      showScore: false
    }
  };

  // Get current config based on review permission
  const currentConfig = reviewPermission === 'none'
    ? layoutConfig.none
    : layoutConfig[reviewPermission][hasPassed ? 'passed' : 'failed'];

  // Icon rendering helper - Responsive SVG badges
  const renderIcon = (iconType) => {
    switch (iconType) {
      case 'badge':
        return (
          <div className="relative inline-flex">
            <img
              src={badgeAwardIcon}
              alt="Success Badge"
              className="w-16 h-16 sm:w-18 sm:h-18 object-contain drop-shadow-2xl animate-in zoom-in-95 duration-500"
            />
            {/* Subtle glow effect */}
            <div className="absolute inset-0 bg-amber-400/20 rounded-full blur-2xl animate-pulse"></div>
          </div>
        );
      case 'book':
        return (
          <div className="relative inline-flex">
            <img
              src={failAlertIcon}
              alt="Try Again"
              className="w-16 h-16 sm:w-18 sm:h-18 object-contain drop-shadow-xl animate-in zoom-in-95 duration-500"
            />
          </div>
        );
      case 'party':
        return (
          <div className="relative inline-flex">
            <img
              src={badgeAwardIcon}
              alt="Completed"
              className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 object-contain drop-shadow-2xl animate-in zoom-in-95 duration-500 opacity-90"
            />
            <div className="absolute inset-0 bg-purple-400/20 rounded-full blur-2xl animate-pulse"></div>
          </div>
        );
      case 'thumbs':
        return (
          <div className="relative inline-flex">
            <img
              src={failAlertIcon}
              alt="Submitted"
              className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 object-contain drop-shadow-xl animate-in zoom-in-95 duration-500 opacity-75"
            />
          </div>
        );
      case 'checkmark':
        return (
          <div className="relative inline-flex">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-2xl animate-in zoom-in-95 duration-500">
              <svg className="w-12 h-12 sm:w-14 sm:h-14 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            {/* Subtle glow effect */}
            <div className="absolute inset-0 bg-emerald-400/20 rounded-full blur-2xl animate-pulse"></div>
          </div>
        );
      default:
        return null;
    }
  };

  // Header gradient based on mode
  const getHeaderGradient = () => {
    if (reviewPermission === 'none') {
      return 'bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100';
    }
    if (reviewPermission === 'partial') {
      return hasPassed
        ? 'bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100'
        : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100';
    }
    return hasPassed
      ? 'bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100'
      : 'bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200';
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        // Prevent closing modal when clicking backdrop
        if (e.target === e.currentTarget) {
          e.stopPropagation();
        }
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-sm w-full animate-in zoom-in-95 duration-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Section - Compact padding */}
        <div className={`p-5 text-center ${getHeaderGradient()}`}>
          {/* Icon/Badge - Smaller sizing */}
          <div className="mb-3">
            {renderIcon(currentConfig.icon)}
          </div>

          {/* Title - Smaller typography */}
          <h2 className="text-xl sm:text-2xl font-bold mb-2 text-slate-900">
            {currentConfig.title}
          </h2>

          {/* Body Text - Smaller sizing */}
          <p className="text-xs sm:text-sm text-slate-600 mb-3 leading-relaxed px-2">
            {currentConfig.body}
          </p>

          {/* Score - Smaller sizing */}
          {currentConfig.showScore && (
            <>
              <p className={`text-3xl sm:text-4xl font-black mb-1 ${
                reviewPermission === 'partial'
                  ? 'text-slate-900'
                  : hasPassed ? 'text-emerald-700' : 'text-slate-700'
              }`}>
                {scoreData.percentage}%
              </p>

              {/* Passing score info */}
              {passingScore !== null && (
                <p className="text-xs font-medium text-slate-600">
                  {hasPassed ? `Passing score: ${passingScore}%` : `Required: ${passingScore}%`}
                </p>
              )}
            </>
          )}
        </div>

        {/* Stats Section - Only if showStats is true - Responsive grid */}
        {currentConfig.showStats && (
          <div className="px-4 sm:px-6 md:px-8 py-6 bg-slate-50">
            <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 mb-6">
              <div className="text-center p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow-sm">
                <div className="text-2xl sm:text-3xl md:text-3xl font-bold text-slate-900">{scoreData.totalScore}</div>
                <div className="text-xs sm:text-sm text-slate-600 mt-1">Points</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow-sm">
                <div className="text-2xl sm:text-3xl md:text-3xl font-bold text-emerald-600">
                  {scoreData.questionResults.filter(r => r.isCorrect).length}
                </div>
                <div className="text-xs sm:text-sm text-emerald-600 mt-1">Correct</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-white rounded-lg sm:rounded-xl shadow-sm">
                <div className="text-2xl sm:text-3xl md:text-3xl font-bold text-red-600">
                  {scoreData.questionResults.filter(r => !r.isCorrect).length}
                </div>
                <div className="text-xs sm:text-sm text-red-600 mt-1">Wrong</div>
              </div>
            </div>
          </div>
        )}

        {/* Button Section - Compact padding */}
        <div className={currentConfig.showStats ? 'px-5 pb-5' : 'p-5'}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Results modal button clicked', { buttonText: currentConfig.buttonText, hasAction: !!currentConfig.buttonAction });
              if (currentConfig.buttonAction && typeof currentConfig.buttonAction === 'function') {
                currentConfig.buttonAction();
              } else {
                console.error('Button action not found or not a function', currentConfig);
              }
            }}
            className={`w-full px-5 py-3 text-white rounded-xl text-sm font-semibold transition-all shadow-lg hover:shadow-xl active:scale-[0.98] ${
              reviewPermission === 'full' || (hasPassed && reviewPermission === 'partial') || reviewPermission === 'none'
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-slate-900 hover:bg-slate-800'
            }`}
          >
            {currentConfig.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TakeQuiz;
